import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import './organization-editor.css';
import { OWNER_SEARCH_DEBOUNCE_MS } from '../../../constants/organization';
import { useOwnerCandidatesQuery } from '../../../hooks/useDashboardQueries';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import { OwnerCandidate } from '../../../types/organization';
import { formatOwnerLabel } from '../../../utils/formatOwnerLabel';
import { parseApiError } from '../../../utils/parseApiError';

interface OwnerUserSearchProps {
  value: OwnerCandidate | null;
  onChange: (user: OwnerCandidate | null) => void;
  disabled?: boolean;
}

/** Two-letter avatar text: email local part, else the phone's last digits. */
const ownerInitials = (owner: OwnerCandidate): string => {
  if (owner.email) {
    return owner.email.slice(0, 2).toUpperCase();
  }
  if (owner.phone) {
    return owner.phone.slice(-2);
  }
  return '?';
};

const OwnerIdentity = ({ owner }: { owner: OwnerCandidate }) => (
  <>
    <span className="owner-avatar" aria-hidden="true">
      {ownerInitials(owner)}
    </span>
    <span className="owner-identity">
      <span className="owner-primary">{formatOwnerLabel(owner)}</span>
      {owner.email && owner.phone && <span className="owner-secondary">{owner.phone}</span>}
    </span>
  </>
);

/**
 * Debounced email/phone user lookup for picking an organization owner.
 * "Change" clears the pick; the previous owner stays one click away until a
 * new one is chosen.
 */
export const OwnerUserSearch = ({ value, onChange, disabled }: OwnerUserSearchProps) => {
  const { t } = useTranslation();
  const [term, setTerm] = useState('');
  const [previous, setPrevious] = useState<OwnerCandidate | null>(null);
  const debouncedTerm = useDebouncedValue(term, OWNER_SEARCH_DEBOUNCE_MS);

  const {
    data: options = [],
    isFetching,
    error,
  } = useOwnerCandidatesQuery(debouncedTerm);

  const pick = (candidate: OwnerCandidate) => {
    setTerm('');
    setPrevious(null);
    onChange(candidate);
  };

  if (value) {
    return (
      <div className="owner-selected">
        <OwnerIdentity owner={value} />
        <button
          type="button"
          className="org-btn org-btn-sm"
          disabled={disabled}
          onClick={() => {
            setPrevious(value);
            onChange(null);
          }}
        >
          <span className="material-symbols-outlined" aria-hidden="true">swap_horiz</span>
          {t('dashboard.organizations.changeOwner')}
        </button>
      </div>
    );
  }

  const hasTerm = term.trim().length > 0;
  // Results belong to the debounced term; until it catches up with the input
  // there is nothing to report yet.
  const settled = debouncedTerm === term;
  const showNoResults =
    settled && !isFetching && !error && hasTerm && options.length === 0;

  return (
    <div className="owner-picker">
      <div className="owner-picker-field">
        <span className="material-symbols-outlined" aria-hidden="true">search</span>
        <input
          type="text"
          className="owner-picker-input"
          placeholder={t('dashboard.organizations.ownerSearchPlaceholder')}
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          aria-label={t('dashboard.organizations.ownerSearchPlaceholder')}
          disabled={disabled}
          autoFocus={previous !== null}
        />
        {isFetching && <span className="owner-picker-spinner" aria-hidden="true" />}
      </div>

      {!isFetching && error != null && (
        <div role="alert" className="owner-picker-note owner-picker-note-error">
          {parseApiError(error, t('dashboard.organizations.ownerSearchError')).message}
        </div>
      )}
      {showNoResults && (
        <div className="owner-picker-note">{t('dashboard.common.noResults')}</div>
      )}
      {options.length > 0 && hasTerm && (
        <ul
          role="listbox"
          aria-label={t('dashboard.organizations.ownerLabel')}
          className="owner-picker-options"
        >
          {options.map((candidate) => (
            <li key={candidate.id} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={false}
                className="owner-picker-option"
                onClick={() => pick(candidate)}
              >
                <OwnerIdentity owner={candidate} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {previous && (
        <button type="button" className="owner-keep" onClick={() => pick(previous)}>
          <span className="material-symbols-outlined" aria-hidden="true">undo</span>
          {t('dashboard.organizations.keepOwner', { owner: formatOwnerLabel(previous) })}
        </button>
      )}
    </div>
  );
};

export default OwnerUserSearch;
